/**
 * MaintenanceDashboard.jsx
 * Dashboard de Manutenção - Estilo SlideTeam Templates
 *
 * Features:
 * - Service reminders (Overdue/Due soon/Upcoming)
 * - Critical faults com códigos OBD
 * - Work orders por status
 * - Tendência de custos de manutenção
 * - Inspection summary
 * - Vehicle health overview
 */

import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Button,
  Avatar,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  DirectionsCar as CarIcon,
  Speed as SpeedIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
  HealthAndSafety as HealthIcon,
  NotificationsActive as AlertIcon,
  OilBarrel as OilIcon,
  BatteryChargingFull as BatteryIcon,
  AirlineSeatReclineNormal as TireIcon,
} from '@mui/icons-material';
import ApexCharts from 'apexcharts';
import { useTheme } from '@mui/material/styles';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';

// ============================================================================
// Service Reminder Card
// ============================================================================

const ServiceReminderCard = ({
  type, vehicle, service, dueDate, kmRemaining, status,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'overdue':
        return { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Vencido', icon: <ErrorIcon /> };
      case 'due_soon':
        return { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Próximo', icon: <WarningIcon /> };
      case 'upcoming':
        return { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', label: 'Programado', icon: <ScheduleIcon /> };
      default:
        return { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'OK', icon: <CheckIcon /> };
    }
  };

  const config = getStatusConfig();

  const getServiceIcon = () => {
    switch (type) {
      case 'oil': return <OilIcon />;
      case 'tire': return <TireIcon />;
      case 'battery': return <BatteryIcon />;
      case 'brake': return <BuildIcon />;
      default: return <SettingsIcon />;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        bgcolor: config.bg,
        border: `1px solid ${config.color}30`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Avatar sx={{ bgcolor: config.color, width: 44, height: 44 }}>
        {getServiceIcon()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={600} noWrap>
          {vehicle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {service}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            <CalendarIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
            {dueDate}
          </Typography>
          {kmRemaining !== undefined && (
            <Typography variant="caption" color="text.secondary">
              <SpeedIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
              {kmRemaining.toLocaleString()} km
            </Typography>
          )}
        </Box>
      </Box>
      <Chip
        size="small"
        icon={config.icon}
        label={config.label}
        sx={{
          bgcolor: config.color,
          color: '#fff',
          fontWeight: 600,
          '& .MuiChip-icon': { color: '#fff' },
        }}
      />
    </Paper>
  );
};

// ============================================================================
// Fault Code Card
// ============================================================================

const FaultCodeCard = ({ code, description, severity, vehicle, timestamp }) => {
  const getSeverityConfig = () => {
    switch (severity) {
      case 'critical':
        return { color: '#ef4444', label: 'Crítico' };
      case 'warning':
        return { color: '#f59e0b', label: 'Alerta' };
      default:
        return { color: '#3b82f6', label: 'Info' };
    }
  };

  const config = getSeverityConfig();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1,
        borderRadius: 2,
        borderLeft: `4px solid ${config.color}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            bgcolor: `${config.color}20`,
            color: config.color,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontWeight: 700,
            fontFamily: 'monospace',
            fontSize: 14,
          }}
        >
          {code}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {vehicle} • {timestamp}
          </Typography>
        </Box>
        <Chip size="small" label={config.label} sx={{ bgcolor: `${config.color}20`, color: config.color }} />
      </Box>
    </Paper>
  );
};

// ============================================================================
// Work Order Summary
// ============================================================================

const WorkOrderSummary = ({ pending, inProgress, completed }) => {
  const total = pending + inProgress + completed;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'rgba(245,158,11,0.1)', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight={800} color="#f59e0b">
            {pending}
          </Typography>
          <Typography variant="caption" color="text.secondary">Pendentes</Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'rgba(59,130,246,0.1)', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight={800} color="#3b82f6">
            {inProgress}
          </Typography>
          <Typography variant="caption" color="text.secondary">Em Andamento</Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'rgba(34,197,94,0.1)', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight={800} color="#22c55e">
            {completed}
          </Typography>
          <Typography variant="caption" color="text.secondary">Concluídas</Typography>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.1)' }}>
        <Box sx={{ width: `${(pending / total) * 100}%`, bgcolor: '#f59e0b' }} />
        <Box sx={{ width: `${(inProgress / total) * 100}%`, bgcolor: '#3b82f6' }} />
        <Box sx={{ width: `${(completed / total) * 100}%`, bgcolor: '#22c55e' }} />
      </Box>
    </Box>
  );
};

// ============================================================================
// Cost Trend Chart Component
// ============================================================================

const CostTrendSection = ({ data, chartRef, isDark, textColor }) => {
  useEffect(() => {
    if (!chartRef.current || !data.length) return;

    const chart = new ApexCharts(chartRef.current, {
      chart: {
        type: 'bar',
        height: 220,
        background: 'transparent',
        foreColor: textColor,
        toolbar: { show: false },
        stacked: true,
      },
      series: [
        { name: 'Manutenção Preventiva', data: data.map((d) => d.preventive) },
        { name: 'Manutenção Corretiva', data: data.map((d) => d.corrective) },
      ],
      xaxis: {
        categories: data.map((d) => d.month),
        labels: { style: { colors: textColor } },
      },
      yaxis: {
        labels: {
          style: { colors: textColor },
          formatter: (val) => `R$ ${(val / 1000).toFixed(0)}k`,
        },
      },
      colors: ['#22c55e', '#ef4444'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '60%',
        },
      },
      legend: {
        position: 'top',
        labels: { colors: textColor },
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (val) => `R$ ${val.toLocaleString()}` },
      },
    });

    chart.render();
    return () => chart.destroy();
  }, [data, chartRef, isDark, textColor]);

  return <Box ref={chartRef} />;
};

// ============================================================================
// Vehicle Health Card
// ============================================================================

const VehicleHealthCard = ({ vehicle, health, issues, lastService, nextService }) => {
  const getHealthColor = (h) => {
    if (h >= 80) return '#22c55e';
    if (h >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const color = getHealthColor(health);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Avatar sx={{ bgcolor: `${color}20`, color }}>
          <CarIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {vehicle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Última manutenção: {lastService}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" fontWeight={800} sx={{ color }}>
            {health}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Saúde
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={health}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: `${color}20`,
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />

      {issues > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
          <Typography variant="caption" color="text.secondary">
            {issues} pendências detectadas
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Próxima manutenção: {nextService}
      </Typography>
    </Paper>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const MaintenanceDashboard = () => {
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const t = useTranslation();
  const navigate = useNavigate();

  const isDark = theme.palette.mode === 'dark';
  const textColor = theme.palette.text.primary;

  // State
  const [loading, setLoading] = useState(true);
  const [_maintenances, setMaintenances] = useState([]);
  const [_devices, setDevices] = useState([]);

  // Chart refs
  const costChartRef = React.useRef(null);

  // Load data
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const [maintenancesRes, devicesRes] = await Promise.all([
        fetch('/api/maintenances'),
        fetch('/api/devices'),
      ]);

      if (maintenancesRes.ok) {
        setMaintenances(await maintenancesRes.json());
      }
      if (devicesRes.ok) {
        setDevices(await devicesRes.json());
      }
    } catch (error) {
      console.error('Failed to load maintenance data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mock data for demo (in real app, calculate from maintenances)
  const serviceReminders = useMemo(() => [
    { type: 'oil', vehicle: 'ABC-1234', service: 'Troca de Óleo', dueDate: '15/02/2026', kmRemaining: -500, status: 'overdue' },
    { type: 'tire', vehicle: 'DEF-5678', service: 'Rodízio de Pneus', dueDate: '20/02/2026', kmRemaining: 800, status: 'due_soon' },
    { type: 'brake', vehicle: 'GHI-9012', service: 'Revisão de Freios', dueDate: '28/02/2026', kmRemaining: 2500, status: 'upcoming' },
    { type: 'battery', vehicle: 'JKL-3456', service: 'Verificar Bateria', dueDate: '05/03/2026', kmRemaining: 5000, status: 'upcoming' },
    { type: 'oil', vehicle: 'MNO-7890', service: 'Troca de Óleo', dueDate: '10/02/2026', kmRemaining: -1200, status: 'overdue' },
  ], []);

  const faultCodes = useMemo(() => [
    { code: 'P0171', description: 'Sistema muito pobre (Banco 1)', severity: 'warning', vehicle: 'ABC-1234', timestamp: 'Hoje, 14:30' },
    { code: 'P0300', description: 'Falhas de ignição aleatórias detectadas', severity: 'critical', vehicle: 'DEF-5678', timestamp: 'Hoje, 10:15' },
    { code: 'P0420', description: 'Eficiência do catalisador abaixo do limite', severity: 'warning', vehicle: 'GHI-9012', timestamp: 'Ontem, 18:45' },
    { code: 'P0128', description: 'Termostato - temperatura do líquido de arrefecimento', severity: 'info', vehicle: 'JKL-3456', timestamp: 'Ontem, 09:20' },
  ], []);

  const workOrders = useMemo(() => ({
    pending: 8,
    inProgress: 5,
    completed: 42,
  }), []);

  const costData = useMemo(() => [
    { month: 'Set', preventive: 12000, corrective: 8000 },
    { month: 'Out', preventive: 15000, corrective: 5000 },
    { month: 'Nov', preventive: 11000, corrective: 12000 },
    { month: 'Dez', preventive: 18000, corrective: 6000 },
    { month: 'Jan', preventive: 14000, corrective: 9000 },
    { month: 'Fev', preventive: 16000, corrective: 4000 },
  ], []);

  const vehicleHealth = useMemo(() => [
    { vehicle: 'ABC-1234', health: 45, issues: 3, lastService: '10/01/2026', nextService: '15/02/2026' },
    { vehicle: 'DEF-5678', health: 72, issues: 1, lastService: '20/01/2026', nextService: '20/02/2026' },
    { vehicle: 'GHI-9012', health: 88, issues: 0, lastService: '05/02/2026', nextService: '05/03/2026' },
    { vehicle: 'JKL-3456', health: 95, issues: 0, lastService: '01/02/2026', nextService: '01/03/2026' },
  ], []);

  // KPI calculations
  const kpis = useMemo(() => {
    const overdue = serviceReminders.filter((s) => s.status === 'overdue').length;
    const dueSoon = serviceReminders.filter((s) => s.status === 'due_soon').length;
    const criticalFaults = faultCodes.filter((f) => f.severity === 'critical').length;
    const totalCost = costData.reduce((acc, d) => acc + d.preventive + d.corrective, 0);
    const avgHealth = Math.round(vehicleHealth.reduce((acc, v) => acc + v.health, 0) / vehicleHealth.length);

    return {
      overdue,
      dueSoon,
      criticalFaults,
      totalCost,
      avgHealth,
      pendingOrders: workOrders.pending,
    };
  }, [serviceReminders, faultCodes, costData, vehicleHealth, workOrders]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: isDark ? '#0d1117' : '#f6f8fa',
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
          {t('maintenanceDashboard') || 'Dashboard de Manutenção'}
        </Typography>
        <Tooltip title={t('sharedRefresh') || 'Atualizar'}>
          <span>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <ErrorIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
            <Typography variant="h4" fontWeight={800} color="#ef4444">
              {kpis.overdue}
            </Typography>
            <Typography variant="caption" color="text.secondary">Vencidos</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <WarningIcon sx={{ fontSize: 32, color: '#f59e0b', mb: 1 }} />
            <Typography variant="h4" fontWeight={800} color="#f59e0b">
              {kpis.dueSoon}
            </Typography>
            <Typography variant="caption" color="text.secondary">Próximos</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <AlertIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
            <Typography variant="h4" fontWeight={800} color="#ef4444">
              {kpis.criticalFaults}
            </Typography>
            <Typography variant="caption" color="text.secondary">Falhas Críticas</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
            <AssignmentIcon sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
            <Typography variant="h4" fontWeight={800} color="#3b82f6">
              {kpis.pendingOrders}
            </Typography>
            <Typography variant="caption" color="text.secondary">Ordens Pendentes</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
            <HealthIcon sx={{ fontSize: 32, color: '#22c55e', mb: 1 }} />
            <Typography variant="h4" fontWeight={800} color="#22c55e">
              {kpis.avgHealth}%
            </Typography>
            <Typography variant="caption" color="text.secondary">Saúde Média</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(156,163,175,0.1)', border: '1px solid rgba(156,163,175,0.2)' }}>
            <MoneyIcon sx={{ fontSize: 32, color: '#6b7280', mb: 1 }} />
            <Typography variant="h5" fontWeight={800} color="text.primary">
              R$ {(kpis.totalCost / 1000).toFixed(0)}k
            </Typography>
            <Typography variant="caption" color="text.secondary">Custo Total (6m)</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Left Column - Service Reminders */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Lembretes de Serviço
              </Typography>
              <Chip size="small" label={serviceReminders.length} color="primary" />
            </Box>

            {loading ? (
              [...Array(4)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 1.5 }} />
              ))
            ) : (
              serviceReminders.map((reminder, index) => (
                <ServiceReminderCard key={index} {...reminder} tokens={tokens} />
              ))
            )}

            <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/settings/maintenances')}>
              Ver Todas as Manutenções
            </Button>
          </Paper>
        </Grid>

        {/* Center Column - Fault Codes & Work Orders */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Fault Codes */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Códigos de Falha (OBD)
              </Typography>
              <Chip
                size="small"
                icon={<ErrorIcon />}
                label={`${faultCodes.filter((f) => f.severity === 'critical').length} críticos`}
                color="error"
              />
            </Box>

            {loading ? (
              [...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={60} sx={{ mb: 1 }} />
              ))
            ) : (
              faultCodes.map((fault, index) => (
                <FaultCodeCard key={index} {...fault} />
              ))
            )}
          </Paper>

          {/* Work Orders Summary */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Ordens de Serviço
            </Typography>
            <WorkOrderSummary {...workOrders} tokens={tokens} />
          </Paper>
        </Grid>

        {/* Right Column - Costs & Vehicle Health */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Cost Trend */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Tendência de Custos
            </Typography>
            <CostTrendSection
              data={costData}
              chartRef={costChartRef}
              isDark={isDark}
              textColor={textColor}
            />
          </Paper>

          {/* Vehicle Health */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Saúde dos Veículos
              </Typography>
              <Chip
                size="small"
                label={`${vehicleHealth.filter((v) => v.health < 60).length} atenção`}
                color="warning"
              />
            </Box>

            {loading ? (
              [...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 1.5 }} />
              ))
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {vehicleHealth.map((vh, index) => (
                  <VehicleHealthCard key={index} {...vh} />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* CSS for animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default MaintenanceDashboard;
